import { Autocomplete, CircularProgress, FormControl, FormHelperText, FormLabel, TextField } from '@mui/material';
import { FC, FocusEventHandler, ReactNode, SyntheticEvent } from 'react';
import { CustomerDto } from '../../dtos';
import { useGetCustomerQuery } from '../../store/generated/generatedApi';
import { emptyGuid } from '../../util';

export interface ICustomerSelectProps {
    // Legacy API - for backward compatibility
    selectedCustomerId?: string;
    handleSelectedCustomerChange?: (selectedCustomer?: CustomerDto) => void;
    // New API - IEntityAutocomplete style
    value?: CustomerDto | null;
    onChange?: (event: React.SyntheticEvent<Element, Event>, value: CustomerDto | null) => void;
    // Common props
    required?: boolean;
    errorMessage?: string;
    error?: boolean;
    onBlur?: FocusEventHandler<HTMLDivElement>;
    disabled?: boolean;
    isLoading?: boolean;
    endAdornment?: ReactNode;
    // Wrapper control
    includeFormControl?: boolean;
    label?: string;
}

export const CustomerSelect: FC<ICustomerSelectProps> = props => {
    const { 
        selectedCustomerId, 
        handleSelectedCustomerChange,
        value,
        onChange,
        required, 
        errorMessage, 
        error,
        onBlur, 
        disabled,
        isLoading: externalIsLoading,
        endAdornment,
        includeFormControl = true,
        label = 'Customer'
    } = props;
    
    const { data: customers, isLoading: isLoadingCustomers } = useGetCustomerQuery({
        sorting: ['NAME+'],
        page: 0,
        pageSize: 10000,
        includeInactive: false
    });

    const isLoading = externalIsLoading ?? isLoadingCustomers;

    // Determine which API is being used
    const isLegacyAPI = selectedCustomerId !== undefined && handleSelectedCustomerChange !== undefined;
    const isNewAPI = value !== undefined && onChange !== undefined;

    const handleSelectChange = (_: SyntheticEvent<Element, Event>, selectedValue: CustomerDto | null) => {
        if (isLegacyAPI && handleSelectedCustomerChange) {
            handleSelectedCustomerChange(selectedValue ?? undefined);
        } else if (isNewAPI && onChange) {
            onChange(_, selectedValue);
        }
    }

    const getSelectedCustomer = (): CustomerDto | null => {
        if (isNewAPI) {
            return value ?? null;
        } else if (isLegacyAPI) {
            if (selectedCustomerId === '' || selectedCustomerId === emptyGuid) {
                return null;
            }
            return customers?.pageResults.find(x => x.id === selectedCustomerId) ?? null;
        }
        return null;
    }

    const autocomplete = (
        <Autocomplete
            value={getSelectedCustomer()}
            onChange={handleSelectChange}
            loading={isLoading}
            options={customers?.pageResults ?? []}
            getOptionLabel={(x) => {
                const customerNumber = x.customerNumber ? ` [${x.customerNumber}]` : '';
                return `${x.name}${customerNumber}`;
            }}
            filterOptions={(options, { inputValue }) => {
                const searchText = inputValue.toLowerCase();
                return options.filter((option) => {
                    const nameMatch = option.name?.toLowerCase().includes(searchText) ?? false;
                    const numberMatch = option.customerNumber?.toLowerCase().includes(searchText) ?? false;
                    return nameMatch || numberMatch;
                });
            }}
            renderInput={(params) => (
                <TextField
                    error={error ?? !!errorMessage}
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                                {params.InputProps.endAdornment}
                                {endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            onBlur={onBlur}
            disabled={disabled}
        />
    );

    if (!includeFormControl) {
        return autocomplete;
    }

    return (
        <FormControl error={!!errorMessage} fullWidth required={required} disabled={disabled}>
            <FormLabel>{label}</FormLabel>
            {autocomplete}
            {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
        </FormControl>
    )
}